import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ChevronLeft, Mail, Phone } from "lucide-react";
import { AppHeader, SourceNote } from "@/components/Layout";
import { api, errMsg, tokens } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const ChannelPick = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="How should we send your code?">
    <button
      type="button"
      data-testid="login-channel-email"
      onClick={() => onChange("email")}
      className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 text-sm font-semibold transition-colors ${
        value === "email" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
      }`}
    >
      <Mail className="h-4 w-4" /> Email <span className="text-[10px] font-normal opacity-70">(default)</span>
    </button>
    <button
      type="button"
      data-testid="login-channel-sms"
      onClick={() => onChange("sms")}
      className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 text-sm font-semibold transition-colors ${
        value === "sms" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
      }`}
    >
      <Phone className="h-4 w-4" /> SMS
    </button>
  </div>
);

export default function Login() {
  useSeo({
    title: "Log in",
    description: "Log in to Food Recall Alerts with your Australian mobile or email.",
    path: "/login",
    noindex: true,
  });
  const nav = useNavigate();
  // step 1: identifier + channel, step 2: code, step 3: existing-user email backfill
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [channel, setChannel] = useState("email");
  const [sentChannel, setSentChannel] = useState("email");
  const [sentTo, setSentTo] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [missingEmail, setMissingEmail] = useState("");

  const sendCode = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/request-code", { identifier, channel, mode: "login" });
      setDevCode(r.dev_code || null);
      setSentChannel(r.channel || channel);
      setSentTo(r.destination || identifier);
      setStep(2);
      toast.success(r.mocked ? "Code generated (SMS mocked)" : `We sent you a code via ${(r.channel || channel) === "email" ? "email" : "SMS"}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/verify", { identifier, code });
      tokens.set(r.token);
      // Existing user without an email on file -> force them to add one before continuing.
      if (r.user.onboarded && !r.user.email) {
        setStep(3);
        return;
      }
      nav(r.user.onboarded ? "/alerts" : "/join");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const saveEmail = async () => {
    setBusy(true);
    try {
      const updated = await api.put("/me", { email: missingEmail });
      toast.success("Email saved to your account");
      nav(updated.onboarded ? "/alerts" : "/join");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const idOk = identifier.trim().length >= 6;
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(missingEmail);
  const sentWhere = sentChannel === "email" ? sentTo : identifier;

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <div className="mx-auto max-w-md px-5 py-12">
        <button
          data-testid="login-back"
          onClick={() => (step > 1 ? setStep(step - 1) : nav("/"))}
          className="mb-6 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="rounded-md border border-slate-200 bg-white p-6 sm:p-8"
          data-testid="login-card"
        >
          {step === 1 && (
            <div className="space-y-5">
              <h1 className="text-2xl font-semibold text-slate-900">Log in</h1>
              <p className="text-sm text-slate-600">
                Enter the mobile number or email on your account and we'll send you a code.
              </p>
              <div>
                <label className="label-caps" htmlFor="login-id">Mobile or email</label>
                <input
                  id="login-id"
                  data-testid="login-identifier-input"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="04XX XXX XXX or you@example.com"
                  autoComplete="username"
                  className="mt-2 w-full rounded-md border border-slate-300 px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
              </div>
              <div>
                <p className="label-caps mb-2">Send the code via</p>
                <ChannelPick value={channel} onChange={setChannel} />
              </div>
              <button
                data-testid="login-send-code-btn"
                disabled={busy || !idOk}
                onClick={sendCode}
                className="w-full rounded-md bg-slate-900 px-5 py-3 font-semibold text-white disabled:opacity-40"
              >
                {busy ? "Sending..." : "Send login code"}
              </button>
              <p className="text-sm text-slate-500">
                No account yet?{" "}
                <Link to="/join" data-testid="login-to-signup-link" className="font-semibold text-red-700 hover:underline">
                  Get recall alerts
                </Link>
              </p>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <h1 className="text-2xl font-semibold text-slate-900">Enter your code</h1>
              <p className="text-sm text-slate-600" data-testid="login-sent-note">
                We sent a code via {sentChannel === "email" ? "email" : "SMS"} to{" "}
                <span className="font-medium text-slate-900">{sentWhere}</span>.
              </p>
              {devCode && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="login-dev-code">
                  SMS is mocked. Your code is <b className="font-mono">{devCode}</b>.
                </div>
              )}
              <input
                data-testid="login-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                maxLength={6}
                placeholder="123456"
                className="w-full rounded-md border border-slate-300 px-4 py-3 text-center text-2xl font-mono tracking-[0.4em] focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
              <button
                data-testid="login-verify-btn"
                disabled={busy || code.length < 6}
                onClick={verify}
                className="w-full rounded-md bg-red-700 px-5 py-3 font-semibold text-white hover:bg-red-800 disabled:opacity-40"
              >
                {busy ? "Checking..." : "Log in"}
              </button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="login-add-email-step">
              <h1 className="text-2xl font-semibold text-slate-900">Add your email</h1>
              <p className="text-sm text-slate-600">
                We now send a welcome message and keep an email on your account for important updates.
                Add it to continue.
              </p>
              <div>
                <label className="label-caps" htmlFor="login-add-email">Email address</label>
                <input
                  id="login-add-email"
                  data-testid="login-add-email-input"
                  value={missingEmail}
                  onChange={(e) => setMissingEmail(e.target.value)}
                  placeholder="you@example.com"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  className="mt-2 w-full rounded-md border border-slate-300 px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
              </div>
              <button
                data-testid="login-save-email-btn"
                disabled={busy || !emailOk}
                onClick={saveEmail}
                className="w-full rounded-md bg-slate-900 px-5 py-3 font-semibold text-white disabled:opacity-40"
              >
                {busy ? "Saving..." : "Save and continue"}
              </button>
            </div>
          )}
        </motion.div>
        <SourceNote className="mt-8" />
      </div>
    </div>
  );
}
